import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography,
  Paper, InputAdornment, Alert, Snackbar, CircularProgress
} from '@mui/material';
import { PersonOutline, EmailOutlined, ArrowBack } from '@mui/icons-material';
import api from '../services/api';

const Recuperacion = ({ onVolver }) => {
  // PASO 1: Ingresar usuario
  // PASO 2: Confirmar correo
  const [paso, setPaso] = useState(1);
  const [usuario, setUsuario] = useState('');
  const [correo, setCorreo] = useState('');
  const [correoEnmascarado, setCorreoEnmascarado] = useState('');
  const [personal, setPersonal] = useState('');
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState({ open: false, text: '', severity: 'info' });

  const mostrarMensaje = (text, severity) => setMensaje({ open: true, text, severity });
  const cerrarMensaje = () => setMensaje({ ...mensaje, open: false });

  // PASO 1: Verificar que el usuario existe y mostrar correo enmascarado
  const handleVerificarUsuario = async () => {
    if (!usuario.trim()) {
      mostrarMensaje('Ingrese su usuario', 'warning');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/recuperacion/verificar-usuario', { usuario: usuario.trim() });
      if (response.data.success) {
        setCorreoEnmascarado(response.data.correoEnmascarado);
        setPersonal(response.data.personal);
        setPaso(2);
      }
    } catch (error) {
      mostrarMensaje(error.response?.data?.message || 'Usuario no encontrado', 'error');
    } finally {
      setLoading(false);
    }
  };

  // PASO 2: Validar correo completo y enviar clave temporal
  const handleEnviarClave = async () => {
    if (!correo.trim()) {
      mostrarMensaje('Ingrese su correo completo', 'warning');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/recuperacion/enviar-clave', {
        usuario: usuario.trim(),
        correo: correo.trim()
      });
      if (response.data.success) {
        mostrarMensaje('¡Clave temporal enviada a su correo!', 'success');
        setTimeout(() => onVolver(), 3000);
      }
    } catch (error) {
      mostrarMensaje(error.response?.data?.message || 'Error al enviar correo', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Botón volver */}
      <Button
        startIcon={<ArrowBack />}
        onClick={onVolver}
        sx={{ color: 'rgba(255,255,255,0.7)', mb: 2, '&:hover': { color: 'white' } }}
      >
        Volver al login
      </Button>

      <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 600 }}>
        Recuperar contraseña
      </Typography>

      {/* ⭐ ALERTA DENTRO DEL PANEL - arriba del formulario */}
{mensaje.open && (
  <Alert 
    severity={mensaje.severity} 
    variant="filled"
    onClose={cerrarMensaje}
    sx={{ mb: 2, borderRadius: 1.5 }}
  >
    {mensaje.text}
  </Alert>
)}

      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 3 }}>
        {paso === 1
          ? 'Ingrese su usuario para continuar'
          : `Hola ${personal}, ingrese su correo completo para confirmar`}
      </Typography>

      {/* PASO 1 - Ingresar usuario */}
      {paso === 1 && (
        <>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mb: 0.8, display: 'block', fontWeight: 600, letterSpacing: '1px' }}>
            USUARIO
          </Typography>
          <TextField
            fullWidth
            placeholder="Ingrese su usuario"
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleVerificarUsuario()}
            disabled={loading}
            autoFocus
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.92)',
                borderRadius: 1.5,
                '&:hover': { backgroundColor: 'white' },
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutline sx={{ color: '#888', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleVerificarUsuario}
            disabled={loading}
            sx={{
              py: 1.5,
              fontWeight: 700,
              backgroundColor: '#DC143C',
              '&:hover': { backgroundColor: '#B01030' },
              textTransform: 'none',
              borderRadius: 1.5,
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Continuar'}
          </Button>
        </>
      )}

      {/* PASO 2 - Confirmar correo */}
      {paso === 2 && (
        <>
          {/* Correo enmascarado */}
          <Box sx={{
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: 1.5,
            p: 2,
            mb: 3,
            border: '1px solid rgba(255,255,255,0.15)'
          }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
              Su correo registrado:
            </Typography>
            <Typography variant="body1" sx={{ color: 'white', fontWeight: 600 }}>
              {correoEnmascarado}
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', mb: 0.8, display: 'block', fontWeight: 600, letterSpacing: '1px' }}>
            INGRESE SU CORREO COMPLETO
          </Typography>
          <TextField
            fullWidth
            placeholder="correo@ejemplo.com"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleEnviarClave()}
            disabled={loading}
            autoFocus
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.92)',
                borderRadius: 1.5,
                '&:hover': { backgroundColor: 'white' },
              },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlined sx={{ color: '#888', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleEnviarClave}
            disabled={loading}
            sx={{
              py: 1.5,
              fontWeight: 700,
              backgroundColor: '#DC143C',
              '&:hover': { backgroundColor: '#B01030' },
              textTransform: 'none',
              borderRadius: 1.5,
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Enviar clave temporal'}
          </Button>
        </>
      )}

      {/* Mensajes */}
  {/* Ya no necesitas el Snackbar */}
    </Box>
  );
};

export default Recuperacion;
